// „Passwort vergessen“ und „Neues Passwort“ – beide ohne Anmeldung, im selben
// Rahmen wie die Anmeldeseite.
//
// Die Seite verrät nie, ob es eine Adresse gibt: Nach dem Absenden steht immer
// derselbe Satz da. Verschickt die Installation keine Mails, sagt das Formular
// das gleich zu Beginn, statt jemanden auf eine Mail warten zu lassen.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { kontoApi } from "../api";
import { AuthRahmen } from "../components/Logo";
import { kontoFehler } from "../auth/fehler";

const MIN = 10;

function ZurueckZurAnmeldung(): JSX.Element {
  return (
    <div>
      <Link to="/login" className="text-sm text-muted underline-offset-2 hover:text-primary hover:underline">
        ← Zurück zur Anmeldung
      </Link>
    </div>
  );
}

export function PasswortVergessenPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [mail, setMail] = useState<boolean | null>(null);
  const [gesendet, setGesendet] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    kontoApi.features().then((f) => setMail(f.mail)).catch(() => setMail(true));
  }, []);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await kontoApi.forgot(email.trim());
      setGesendet(r.message);
    } catch (err) {
      setError(kontoFehler(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthRahmen titel="Passwort vergessen? Kommt vor." text="Ein Link per E-Mail, ein neues Passwort – und du bist wieder auf Sendung." punkte={[]}>
      <div className="card space-y-5 p-6 shadow-lg sm:p-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Passwort vergessen</h1>
          <p className="mt-1 text-sm text-muted">Gib die E-Mail-Adresse deines Zugangs ein. Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst.</p>
        </div>

        {mail === false ? (
          <div className="callout callout-warning">
            <div>
              <div className="font-semibold">Keine E-Mails auf dieser Installation</div>
              <div className="mt-0.5 opacity-90">Diese Installation verschickt keine E-Mails – wende dich an deine Radioleitung. Sie kann dir unter „Team &amp; Rollen“ ein neues Passwort setzen.</div>
            </div>
          </div>
        ) : gesendet ? (
          <div className="callout callout-success" role="status">
            <div>
              <div className="font-semibold">Schau in dein Postfach</div>
              <div className="mt-0.5 opacity-90">{gesendet} Nichts angekommen? Auch im Spam-Ordner nachsehen.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="field-label">E-Mail</span>
              <input
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="name@meinradio.de"
              />
            </label>
            {error && <div className="callout callout-danger" role="alert">{error}</div>}
            <button type="submit" disabled={busy || mail === null} className="btn btn-primary btn-lg w-full">
              {busy ? "Bitte warten …" : "Link anfordern"}
            </button>
          </form>
        )}
        <ZurueckZurAnmeldung />
      </div>
    </AuthRahmen>
  );
}

export function PasswortNeuPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Den Token einmal aus der Adresse lesen und dann aus der Adresszeile nehmen:
  // So landet er weder im Verlauf noch als Referrer bei anderen Seiten.
  const [token] = useState(() => params.get("token") ?? "");
  const [pw, setPw] = useState({ neu: "", nochmal: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fertig, setFertig] = useState(false);

  useEffect(() => {
    if (token) window.history.replaceState(null, "", "/passwort-neu");
  }, [token]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (pw.neu !== pw.nochmal) return setError("Die beiden Passwörter sind nicht gleich.");
    setError(null);
    setBusy(true);
    try {
      await kontoApi.reset(token, pw.neu);
      setFertig(true);
    } catch (err) {
      setError(kontoFehler(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthRahmen titel="Ein neues Passwort." text="Danach bist du überall abgemeldet und meldest dich mit dem neuen Passwort wieder an." punkte={[]}>
      <div className="card space-y-5 p-6 shadow-lg sm:p-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Neues Passwort</h1>
          <p className="mt-1 text-sm text-muted">Mindestens {MIN} Zeichen. Ein Satz, den du dir merken kannst, ist besser als ein kurzes Kauderwelsch.</p>
        </div>
        {!token ? (
          <div className="callout callout-danger" role="alert">
            <div>
              <div className="font-semibold">Dieser Link ist unvollständig</div>
              <div className="mt-0.5 opacity-90">Bitte den Link aus der E-Mail vollständig öffnen oder einen neuen anfordern.</div>
            </div>
          </div>
        ) : fertig ? (
          <>
            <div className="callout callout-success" role="status">
              <div>
                <div className="font-semibold">Passwort geändert</div>
                <div className="mt-0.5 opacity-90">Alle Geräte, auf denen du angemeldet warst, sind jetzt abgemeldet.</div>
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-lg w-full" onClick={() => navigate("/login", { replace: true })}>
              Zur Anmeldung
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="field-label">Neues Passwort</span>
              <input type="password" autoComplete="new-password" minLength={MIN} required autoFocus className="input" value={pw.neu} onChange={(e) => setPw({ ...pw, neu: e.target.value })} />
            </label>
            <label className="block">
              <span className="field-label">Noch einmal</span>
              <input type="password" autoComplete="new-password" minLength={MIN} required className="input" value={pw.nochmal} onChange={(e) => setPw({ ...pw, nochmal: e.target.value })} />
            </label>
            {error && (
              <div className="callout callout-danger" role="alert">
                <div>
                  {error}
                  {error.includes("abgelaufen") && (
                    <> <Link to="/passwort-vergessen" className="font-medium underline">Neuen Link anfordern</Link></>
                  )}
                </div>
              </div>
            )}
            <button type="submit" disabled={busy} className="btn btn-primary btn-lg w-full">
              {busy ? "Speichere …" : "Passwort speichern"}
            </button>
          </form>
        )}
        <ZurueckZurAnmeldung />
      </div>
    </AuthRahmen>
  );
}
